import { getDevBearerOverride } from "@/lib/dev-bearer";

/**
 * Ginja SSO / platform constants and helpers (sandbox split).
 * Assisted Onboarding SPA ↔ Ginja parent app.
 */

export const GINJA_PARENT_URL = "https://app.sandbox.ginja.ai";
export const GINJA_PLATFORM_API_BASE = "https://api-sandbox.ginja.ai/api/v1";
export const GINJA_APP_SERVICE_API_BASE = "https://service.ginja.ai/sandbox/api/v1";

export const GINJA_COOKIE_NAME = "ginja_access_token";
export const SSO_COOKIE_SENTINEL = "sso-cookie-auth";

// ─── Types — /me/access ─────────────────────────────────────────────────────

export interface GinjaPermission {
  key?: string;
  name?: string;
}

export interface GinjaFeature {
  key?: string;
  name?: string;
  permissions?: GinjaPermission[];
}

export interface GinjaDepartment {
  key: string;
  name?: string;
  features?: GinjaFeature[];
}

/** `departments[].key` for Assisted Onboarding in `/me/access`. */
export const ASSISTED_ONBOARDING_DEPT_KEY = "assisted_onboarding";

/**
 * Permission keys under that department (features[].permissions[].key).
 * Used with `hasAoPermission` from auth context (Ginja sessions only).
 */
export const AO_PERM = {
  allPolicies: "all_policies",
  getAssignedPolicy: "get_assigned_policy",
  assignment: "assignment",
  viewRmList: "view_rm_list",
  bulkUpload: "bulk_upload",
  addMember: "add_member",
  editMember: "edit_member",
  deleteMember: "delete_member",
  activateMember: "activate_member",
  viewMember: "view_member",
  extendSubscription: "extend_subscription",
  updateMemberStatus: "update_member_status",
} as const;

export type AoPermissionKey = (typeof AO_PERM)[keyof typeof AO_PERM];

export interface GinjaAccessUser {
  user_id?: string | number;
  first_name?: string;
  last_name?: string;
  full_name?: string;
  email?: string;
  user_type?: string;
  is_privileged?: boolean;
}

export interface GinjaAccessContext {
  user: GinjaAccessUser;
  departments?: GinjaDepartment[];
  /** Some gateways return a JWT on this envelope — optional. */
  access_token?: string;
  token?: string;
}

export interface MeAccessApiResponse {
  status?: string;
  message?: string;
  data?: GinjaAccessContext;
  timestamp?: string;
}

export type AppUser = {
  id?: string;
  name: string;
  email: string;
  avatar?: string;
  role?: string;
};

function titleCaseToken(s: string): string {
  return s
    .split(/[\s_]+/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join("_");
}

export function deriveDisplayRole(user: GinjaAccessUser, context?: GinjaAccessContext | null): string {
  if (user.is_privileged) return "Chief_Underwriter";
  const deps = context?.departments ?? [];
  if (
    deps.some(
      (d) => d.key === "assisted_onboarding",
    )
  ) {
    return "Assisted_Onboarding";
  }
  const ut = user.user_type?.trim();
  if (ut) return titleCaseToken(ut);
  return "User";
}

export function mapGinjaAccessContextToAppUser(context: GinjaAccessContext): AppUser {
  const u = context.user;
  const name =
    u.full_name?.trim() ||
    [u.first_name, u.last_name].filter(Boolean).join(" ").trim() ||
    "User";
  const role = deriveDisplayRole(u, context);
  return {
    id: u.user_id != null ? String(u.user_id) : undefined,
    name,
    email: u.email ?? "",
    role,
  };
}

/** Platform `/me/access` `data.user.user_id` (e.g. `EC-USR-000058`), persisted in `userInfo`. */
export function readGinjaUserIdFromStorage(): string | undefined {
  try {
    const raw = localStorage.getItem("userInfo");
    if (!raw) return undefined;
    const u = JSON.parse(raw) as GinjaAccessUser;
    if (u.user_id == null || u.user_id === "") return undefined;
    return String(u.user_id);
  } catch {
    return undefined;
  }
}

/** Parsed `localStorage.accessContext` from `/me/access`. */
export function readAccessContextFromStorage(): GinjaAccessContext | null {
  try {
    const raw = localStorage.getItem("accessContext");
    if (!raw) return null;
    const ctx = JSON.parse(raw) as GinjaAccessContext;
    if (!ctx || typeof ctx !== "object" || !ctx.user) return null;
    return ctx;
  } catch {
    return null;
  }
}

/** All non-empty `permissions[].key` under one department (any feature). */
export function collectDepartmentPermissionKeys(
  context: GinjaAccessContext | null | undefined,
  departmentKey: string,
): Set<string> {
  const out = new Set<string>();
  if (!context?.departments?.length) return out;
  const dept = context.departments.find((d) => d.key === departmentKey);
  if (!dept?.features?.length) return out;
  for (const f of dept.features) {
    for (const p of f.permissions ?? []) {
      const k = typeof p.key === "string" ? p.key.trim() : "";
      if (k) out.add(k);
    }
  }
  return out;
}

export function mapStoredGinjaUserInfoToAppUser(): AppUser | null {
  try {
    const rawUser = localStorage.getItem("userInfo");
    const rawCtx = localStorage.getItem("accessContext");
    if (!rawUser) return null;
    const user = JSON.parse(rawUser) as GinjaAccessUser;
    const context = rawCtx ? (JSON.parse(rawCtx) as GinjaAccessContext) : { user };
    if (!user || typeof user !== "object") return null;
    return mapGinjaAccessContextToAppUser({ ...context, user });
  } catch {
    return null;
  }
}

function looksLikeJwt(s: string): boolean {
  const parts = s.split(".");
  return parts.length === 3 && parts.every((p) => p.length > 0);
}

/**
 * Merge bearer for Ginja session: hash/cookie hint wins for the initial /me/access call.
 */
export function resolveMergedBearer(
  context: GinjaAccessContext,
  hintFromHashOrCookie: string | null,
): string {
  const fromApi = context.access_token || context.token;
  if (fromApi && fromApi !== SSO_COOKIE_SENTINEL) return fromApi;
  if (hintFromHashOrCookie && hintFromHashOrCookie !== SSO_COOKIE_SENTINEL && looksLikeJwt(hintFromHashOrCookie)) {
    return hintFromHashOrCookie;
  }
  return SSO_COOKIE_SENTINEL;
}

export function readCookie(name: string): string | null {
  if (typeof document === "undefined") return null;
  const escaped = name.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const m = document.cookie.match(new RegExp(`(?:^|; )${escaped}=([^;]*)`));
  return m ? decodeURIComponent(m[1]) : null;
}

/** Reads `#token=…` or `#access_token=…`, persists to `localStorage.token`, strips hash. */
export function consumeTokenFromHash(): string | null {
  if (typeof window === "undefined") return null;
  const raw = window.location.hash?.replace(/^#/, "") ?? "";
  if (!raw) return null;
  const params = new URLSearchParams(raw);
  const token = params.get("token") || params.get("access_token");
  if (!token) return null;
  localStorage.setItem("token", token);
  const clean = `${window.location.pathname}${window.location.search}`;
  window.history.replaceState(null, "", clean);
  return token;
}

/**
 * `GET /me/access` with cookies. Pass `null` as the hint to mean “no client-readable token —
 * rely on HttpOnly cookie only” (must not fall through to `??` and pick a stale `localStorage.token`).
 * Omit the argument to resolve the hint from storage yourself (rare).
 */
export async function fetchAccessContext(bearerHint?: string | null): Promise<GinjaAccessContext | null> {
  const hintExplicit = arguments.length > 0;
  const fromLsToken = localStorage.getItem("token");
  const hint = hintExplicit
    ? bearerHint ?? null
    : (fromLsToken && fromLsToken !== SSO_COOKIE_SENTINEL ? fromLsToken : null) ??
      readCookie(GINJA_COOKIE_NAME);

  const authTok = localStorage.getItem("auth-token");
  const devOverride = getDevBearerOverride();
  const bearer =
    devOverride ??
    (hint && hint !== SSO_COOKIE_SENTINEL
      ? hint
      : authTok && authTok !== SSO_COOKIE_SENTINEL
        ? authTok
        : null);

  const url = `${GINJA_PLATFORM_API_BASE}/me/access`;
  const baseHeaders: Record<string, string> = { Accept: "application/json" };

  const doFetch = (authorization: string | null) =>
    fetch(url, {
      method: "GET",
      credentials: "include",
      headers: {
        ...baseHeaders,
        ...(authorization ? { Authorization: `Bearer ${authorization}` } : {}),
      },
    });

  let res = await doFetch(bearer);
  // Stale product/platform JWT in storage can 401 while `ginja_access_token` cookie is valid — retry cookie-only.
  if (res.status === 401 && bearer) {
    res = await doFetch(null);
  }

  if (!res.ok) return null;
  let body: MeAccessApiResponse;
  try {
    body = (await res.json()) as MeAccessApiResponse;
  } catch {
    return null;
  }
  const data = body?.data;
  if (!data?.user) return null;
  return data;
}

export function persistAccessContext(mergedBearer: string, context: GinjaAccessContext): void {
  const u = context.user;
  const appUser = mapGinjaAccessContextToAppUser(context);
  const isRealJwt = mergedBearer !== SSO_COOKIE_SENTINEL;

  if (isRealJwt) {
    localStorage.setItem("auth-token", mergedBearer);
    localStorage.setItem("token", mergedBearer);
  } else {
    localStorage.setItem("auth-token", SSO_COOKIE_SENTINEL);
    localStorage.removeItem("token");
  }

  localStorage.setItem("userInfo", JSON.stringify(u));
  localStorage.setItem("accessContext", JSON.stringify(context));
  localStorage.setItem("name", appUser.name);
  localStorage.setItem("role", deriveDisplayRole(u, context));
  localStorage.setItem("user", JSON.stringify(appUser));

  const hrLike = {
    first_name: u.first_name ?? "",
    last_name: u.last_name ?? "",
    name: appUser.name,
    email: appUser.email,
    team_role: [] as { team: string; role: string }[],
  };
  localStorage.setItem("hrUser", JSON.stringify(hrLike));
}

export function clearGinjaLocalStorage(): void {
  const keys = [
    "auth-token",
    "token",
    "userInfo",
    "name",
    "role",
    "accessContext",
    "user",
    "ginja-brand-settings",
  ];
  for (const k of keys) {
    try {
      localStorage.removeItem(k);
    } catch {
      /* ignore */
    }
  }
}

export function redirectToParentLogin(): void {
  if (typeof window === "undefined") return;
  const returnTo = encodeURIComponent(window.location.href);
  window.location.href = `${GINJA_PARENT_URL}/login?returnTo=${returnTo}`;
}
